import { fieldTypes, FileDocument } from "@ucl/library";
import { FC, useRef } from "react";
import { FieldComplexV2 } from "./FieldComplexV2";
import { COMPLEX_FIELD_KEYS } from "@/constants/fieldKeys";
import "./ComplexFields.scss";
import { BoltDocument, BoltDocumentFileUploaderProps } from "@bolt/document";
import { get, set } from "lodash";
import { IdentificationDocuments } from "@/types/IdentificationDocuments";
import { GovernmentIdType } from "@/types/enums";

export interface IdentificationDocumentsComplexValue {
  entityId?: string;
  entityType?: string;
  governmentIdType?: GovernmentIdType;
  showHeader?: boolean;
}

export interface IdentificationDocumentsComplexFieldProps
  extends fieldTypes.BaseFieldProps<IdentificationDocuments> {
  formValue: any;
  fieldPath: string;
  onValueChange: (newValue: any, newFieldValue: any, fieldProps: any) => void;
}

export const IdentificationDocumentsComplexField: FC<IdentificationDocumentsComplexFieldProps> = (
  props
) => {
  const localComplexValueRef = useRef({ ...props.value });
  const identificationDocumentsComplexValue =
    props.complexFieldValue as IdentificationDocumentsComplexValue;
  const uploaderProps = {
    entityType: identificationDocumentsComplexValue?.entityType,
    entityId: identificationDocumentsComplexValue?.entityId,
  } as BoltDocumentFileUploaderProps;

  const handleSubmit = (path: keyof IdentificationDocuments, valueDocument: any) => {
    const boltDocument = valueDocument as BoltDocument;
    const newComplexValue = { ...localComplexValueRef.current };
    const pathPreviousValue = get(newComplexValue, path);

    // Set new Comma Delimited String for the path
    let pathNewValue = pathPreviousValue
      ? `${pathPreviousValue},${boltDocument.id}`
      : boltDocument.id;

    // Remove duplicates
    pathNewValue = [...new Set(pathNewValue.split(","))].join(",");

    // Update the local complex value
    set(newComplexValue, path, pathNewValue);
    localComplexValueRef.current = { ...newComplexValue };

    // Update the form value
    const newValue = { ...props.formValue };
    set(newValue, props.fieldPath, newComplexValue);
    props.onValueChange({ ...newValue }, newComplexValue, props);
  };

  const handleRemoving = (path: keyof IdentificationDocuments, valueDocument: any) => {
    const boltDocument = valueDocument as BoltDocument;
    const newComplexValue = { ...localComplexValueRef.current };
    const pathPreviousValue = get(newComplexValue, path);

    if (!pathPreviousValue) {
      return; // Nothing to remove
    }

    // Remove the document ID from the comma-delimited string
    const pathArray = pathPreviousValue.split(",").filter((id) => id !== boltDocument.id);
    const pathNewValue = pathArray.join(",");

    // Update the local complex value
    set(newComplexValue, path, pathNewValue);
    localComplexValueRef.current = { ...newComplexValue };

    // Update the form value
    const newValue = { ...props.formValue };
    set(newValue, props.fieldPath, newComplexValue);
    props.onValueChange({ ...newValue }, newComplexValue, props);
  };

  return (
    <section className="complex-field-container financial-application-documents">
      {identificationDocumentsComplexValue?.showHeader && <h4>{props.label}</h4>}
      <div className="complex-field-content">
        <FieldComplexV2
          {...props}
          complexFieldKey={COMPLEX_FIELD_KEYS.IDENTIFICATION_DOCUMENTS.STATE_ISSUED_ID_FRONT}
          complexFieldProps={
            {
              ...uploaderProps,
              hidden:
                identificationDocumentsComplexValue?.governmentIdType !==
                GovernmentIdType.StateIssuedID,
              onSubmit: (value: any) => {
                handleSubmit("stateIssuedIdFront", value);
              },
              onCompleteRemoving: (doc?: FileDocument) => {
                handleRemoving("stateIssuedIdFront", doc);
              },
            } as BoltDocumentFileUploaderProps
          }
        />
        <FieldComplexV2
          {...props}
          complexFieldKey={COMPLEX_FIELD_KEYS.IDENTIFICATION_DOCUMENTS.STATE_ISSUED_ID_BACK}
          complexFieldProps={
            {
              ...uploaderProps,
              hidden:
                identificationDocumentsComplexValue?.governmentIdType !==
                GovernmentIdType.StateIssuedID,
              onSubmit: (value: any) => {
                handleSubmit("stateIssuedIdBack", value);
              },
              onCompleteRemoving: (doc?: FileDocument) => {
                handleRemoving("stateIssuedIdBack", doc);
              },
            } as BoltDocumentFileUploaderProps
          }
        />
        <FieldComplexV2
          {...props}
          complexFieldKey={COMPLEX_FIELD_KEYS.IDENTIFICATION_DOCUMENTS.PASSPORT_BIOGRAPHIC_PAGE}
          complexFieldProps={
            {
              ...uploaderProps,
              hidden:
                identificationDocumentsComplexValue?.governmentIdType !== GovernmentIdType.Passport,
              onSubmit: (value: any) => {
                handleSubmit("passportBiographicPage", value);
              },
              onCompleteRemoving: (doc?: FileDocument) => {
                handleRemoving("passportBiographicPage", doc);
              },
            } as BoltDocumentFileUploaderProps
          }
        />
        <FieldComplexV2
          {...props}
          complexFieldKey={COMPLEX_FIELD_KEYS.IDENTIFICATION_DOCUMENTS.PASSPORT_SIGNATURE_PAGE}
          complexFieldProps={
            {
              ...uploaderProps,
              hidden:
                identificationDocumentsComplexValue?.governmentIdType !== GovernmentIdType.Passport,
              onSubmit: (value: any) => {
                handleSubmit("passportSignaturePage", value);
              },
              onCompleteRemoving: (doc?: FileDocument) => {
                handleRemoving("passportSignaturePage", doc);
              },
            } as BoltDocumentFileUploaderProps
          }
        />
      </div>
    </section>
  );
};
